import _ from "underscore";
import cookie from "web/script/utils/cookie";
import browser from "web/script/utils/browser";
import storage from "web/script/utils/storage";
import uuid from "web/script/utils/uuid";
import environment from "web/script/modules/environment";
import eventEmitter from "web/script/mixins/event-emitter";
import globals from "web/script/modules/globals";
import { BannerType, getBannerCampaign } from "web/script/utils/app-banner-campaign";
import criteoTransport from "./transports/criteo-transport";
import googleAnalyticsTransport from "./transports/google-analytics-transport";
import googleConversionTransport from "./transports/google-conversion-transport";
import cloudFrontAnalyticsTransport from "./transports/cf-analytics-transport";
import agifTransport from "./transports/agif-transport";
import facebookEvents from "./facebook-events";
import facebookTagTransport from "./transports/facebook-tag-transport";
import globalRadioTransport from "./transports/global-radio-transport";
import yahooGeminiTransport from "./transports/yahoo-gemini-transport";
import branchIOTransport from "./transports/branch-io-transport";
import bingConversionTransport from "./transports/bing-conversion-transport";

/**
 * Index of the session id in the analytics cookie.
 * @final
 * @type {number}
 */
const SESSION_ID_INDEX = 0;

/**
 * Page view index in the analytics cookie.
 * @type {number}
 */
const PAGE_VIEW_COUNT_INDEX = 1;

/**
 * Index of the "is returning" value in the analytics cookie.
 * @final
 * @type {number}
 */
const IS_RETURNING_INDEX = 2;

/**
 * Index of the device uid in the analytics cookie.
 * @type {number}
 */
const DEVICE_UID_INDEX = 4;

/**
 * Key used to store analytics cookie
 * @type {string}
 */
const COOKIE_NAME = "analytics";

/**
 * Current cookie version. Keep in Sync with analytics middleware
 * @type {number}
 */
const COOKIE_VERSION = parseInt(environment.get("analyticsCookieVersion", "1"), 10);

/**
 * Function to generate a UUID number to use in Analytics Payloads
 * @returns {string}
 */
export function uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
        let r = (Math.random() * 16) | 0;
        let v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Function to take a URLSearchParams object and get the last entry for a given key
 * Reason for doing that is that we often get paid links with multiple versions of atc_ *
 * parameters. If the key does not exist, it returns null
 * @returns {(string|null)}
 */
function getLastParamOrNull(params, key) {
    return params.getAll(key).slice(-1)[0] || null;
}

class Analytics {
    constructor() {
        this._initInitialPageType(environment.get("pageType"));
        this._initInitialSubPageType(environment.get("pageSubType"));

        // To indicate if app download banner is enabled
        this._isAppBannerEnabled = false;

        if (environment.get("disable_third_party_js") !== true) {
            this.transports = [
                ...this.transports,
                criteoTransport,
                facebookTagTransport,
                globalRadioTransport,
                yahooGeminiTransport,
            ];

            const bannerCampaign = getBannerCampaign(
                this.getInitialPageType(),
                this.getInitialSubPageType()
            );

            this._isAppBannerEnabled =
                bannerCampaign && bannerCampaign.bannerType !== BannerType.NoBanner;

            if (bannerCampaign && bannerCampaign.bannerType === BannerType.Branch) {
                this.transports.push(branchIOTransport);
            }
        }

        // Only send page timing data on first pageview after load.
        this._pageLoadTimingDataSent = false;

        // Don't increment the pageViewsCount until after the first pageViews() call
        this._allowPageViewsCountIncrement = false;

        this.init = function() {
            // this reads data from local storage and the analytics cookie
            this._setInitialState();

            this._initTransports();

            this._send = this._send.bind(this);

            // Browser time from page load, to get more correct event_time
            this._browserTimeStart = new Date();

            // Initial referrer is the document referrer, but this may change while the user is on the page.
            // For example if they open up an overlay or the page changes without reload e.g. search
            this.referrer = document.referrer;
        };
    }
    on = eventEmitter.on;
    off = eventEmitter.off;
    trigger = eventEmitter.trigger;

    /**
     * Array of pending Promises that should be
     * resolved when all `pendingData` has been sent.
     * @type {Promise[]}
     */
    pendingPromiseResolvers = [];

    /**
     * Array of pending data objects. Each object will
     * have `type` and `data` properties.
     * @type {Object[]}
     */
    pendingData = [];

    /**
     * The document referrer. Updated when a page view is tracked for client side rendered pages.
     * @type {string}
     */
    referrer = null;

    /**
     * Array of transport objects.
     *
     * A transport is an object that implements a `send` method and
     * knows how to send data to a particular service.
     *
     * This method will be called with an array of pending data objects,
     * each with `type` and `data` properties. It should always return
     * a Promise that is resolved when the data it needed to send has
     * been successfully sent.
     * @type {Object[]}
     */
    transports = [
        bingConversionTransport,
        googleAnalyticsTransport,
        googleConversionTransport,
        cloudFrontAnalyticsTransport,
        agifTransport,
    ];

    /**
     * The id of the setTimeout used to schedule a send.
     * @type {Number}
     */
    sendTimeout = null;

    TAG_NETWORKS = {
        FACEBOOK: "facebook",
        CRITEO: "criteo",
    };

    /**
     * Returns if branch io is enabled.
     * @returns {Boolean}
     */
    isAppBannerEnabled() {
        return this._isAppBannerEnabled;
    }

    /**
     * Returns a Promise that is resolved when the queue is next empty.
     * If the queue is already empty a resolved Promise will be returned.
     * @returns {Promise}
     */
    whenQueueIsEmpty() {
        if (this.pendingData.length === 0) {
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            this.pendingPromiseResolvers.push(resolve);
        });
    }

    /**
     * Tracks an event.
     * @param {string} category The event category.
     * @param {string} action The event action.
     * @param {string} [label] Optional free text label for the event.
     * @param {Boolean} [nonInteraction=false] This field is used to indicate that the event
     *                                         is not the result of the user interacting with
     *                                         the page. Non-interactive events will not effect
     *                                         bounce rate, use it for things like a pop-up being
     *                                         triggered automatically.
     * @param {dict} [customData=null] Optional information if you need to send customised data.
     */
    event(category, action, label, nonInteraction, customData) {
        if (!_.isString(category) || !_.isString(action)) {
            console.error("Both category and action must be strings.");
            return Promise.resolve();
        }

        // Trimming the event properties to fit
        // our SQL schema char length. See:
        // https://git.lystit.com/lyst/analytics/blob/master/src/lyst_analytics/schema.sql
        if (category) {
            category = category.substring(0, 16);
        }

        if (action) {
            action = action.substring(0, 64);
        }

        if (label == null || label === false) {
            label = "";
        } else {
            // It's possible that the label is a number
            label = (label + "").substring(0, 64);
        }

        this.pendingData.push({
            type: "event",
            data: this._addMetaData({
                // metadata
                _version: "0.0.3",
                _type: "events",

                // events
                category: category.toLowerCase(),
                action: action.toLowerCase(),
                label: label,
                // RedShift doesn't like uppercase but GA wants it, unify when removing GA
                nonInteraction: nonInteraction === true,
                noninteraction: nonInteraction === true,

                page_type: environment.get("pageType"),
                page_sub_type: environment.get("pageSubType"),

                customData: customData || {},
            }),
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Track an ecommerce action using the GA enhanced ecommerce tracking.
     * This method takes the same arguments as the `ga` function.
     * See https://developers.google.com/analytics/devguides/collection/analyticsjs/enhanced-ecommerce for more info.
     * @param args
     * @returns {Promise}
     */
    ecommerce(...args) {
        this.pendingData.push({
            type: "ecommerce",
            data: args,
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Fire retargetting data to Google Adwords. The conversion ID and conversion label
     * are provided by Google Adwords tag set up. Google Ads will provide some HTML code
     * to put on a page to do retargeting. Ignore most of it, but get the conversion ID and
     * label from it.
     *
     * If conversion is not set up correctly, then it will be silently ignored.
     *
     * This function comes from Google's conversion_async.js, which is loaded through
     * Google Tag Manager. Use this function to handle custom events that the Analytics
     * team otherwise cannot target.
     * @param conversionId
     * @param conversionLabel
     */
    retarget(conversionId, conversionLabel) {
        this.pendingData.push({
            type: "adwords_retargetting",
            data: {
                google_conversion_id: conversionId,
                google_conversion_label: conversionLabel,
            },
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Track an event via Facebook conversion tracking. Typically this would track the normal
     * way, which would return from a promise instantly. However, it is possible to wait
     * for the track to be done, but passing true as the third variable. This will use
     * the image way (custom data is still encoded properly).
     * @param eventName - the facebook track event name or ID
     * @param [customData] - any custom data (such as ecommerce params)
     * @param [wait] - when set to True, will wait for an actual image pixel to load
     * @returns {Promise}
     */
    tagEvent(network, eventName, customData, wait) {
        if (_.isUndefined(wait)) {
            wait = false;
        }

        this.pendingData.push({
            type: network + "_event",
            wait: wait,
            data: {
                eventName: eventName,
                customData: customData || {},
            },
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Tracks a page view.
     */
    pageView() {
        // update the page view count and generate a new page view id
        if (this._allowPageViewsCountIncrement) {
            this.options.pageViewCount = this.getPageViewsCount() + 1;
        } else {
            this._allowPageViewsCountIncrement = true;
        }

        // update analytics cookie
        this.updatePageViewInAnalyticsCookie();

        let data = this._getPageViewData();

        // some data may be stored in the environment
        let envData = environment.get("pageViewAnalytics");
        if (envData) {
            _.extend(data, envData);
        }

        const supportsNTAPI =
            window.performance && window.performance.now && window.performance.timing;

        if (!this._pageLoadTimingDataSent && supportsNTAPI) {
            let perf = window.performance.timing;

            let now = Math.round(perf.navigationStart + window.performance.now());
            let loadTimingData = {
                ttfb_ms: perf.responseStart - perf.fetchStart,
                page_load_ms: now - perf.responseStart,
            };

            if (loadTimingData.ttfb_ms > 0 && loadTimingData.page_load_ms > 0) {
                _.extend(data, loadTimingData);
            }

            this._pageLoadTimingDataSent = true;
        }

        this.pendingData.push({
            data,
            type: "page_view",
        });

        this.tagEvent(this.TAG_NETWORKS.FACEBOOK, facebookEvents.PAGE_VIEW, {
            life_cycle_stage: data.life_cycle_stage,
        });

        this.setReferrer(globals.window.location.href, this.options.pageViewId);
        return this._addPromiseAndScheduleSend();
    }

    /*
     * Send an payload with metadata attached
     *
     * @param {String} name - payload name
     * @param {Object} payload - payload value
     */
    trackWithMetaData(name, payload) {
        this.pendingData.push({
            type: name,
            data: this._addMetaData(payload),
        });

        return this._addPromiseAndScheduleSend();
    }

    trackUXScore(uxScoreData) {
        this.pendingData.push({
            type: "ux_score",
            data: this._addMetaData({
                // metadata
                _version: "0.0.3",
                _type: "ux_score",

                score_question: uxScoreData.scoreQuestion,
                score_answer: uxScoreData.scoreAnswer,
                score_maximum: uxScoreData.maxScore,
                additional_question: uxScoreData.additionalQuestion,
                additional_answer: uxScoreData.additionalAnswer,
            }),
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Track a social sharing event.
     * @param {string} network The sharing network, i.e 'facebook'
     * @param {string} action What the user did, i.e 'like'
     * @param {string} target What was shared, this should be a url
     *                        but can just be text if appropriate.
     * @returns {Promise}
     */
    social(network, action, target) {
        this.pendingData.push({
            type: "social",
            data: this._addMetaData({
                network: network,
                action: action,
                target: target,
            }),
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Tracks a search term.
     * @param {String} term
     */
    searchTerm(term) {
        this.pendingData.push({
            type: "search_term",
            data: term,
        });

        return this._addPromiseAndScheduleSend();
    }

    /**
     * Returns the Lyst analytics session id.
     * @returns {string}
     */
    getSessionId() {
        return this.options.sessionId;
    }

    /**
     * Returns the Lyst analytics device id.
     * @returns {string}
     */
    getDeviceUid() {
        return this.options.deviceId;
    }

    /**
     * Returns true if this user is a returning visitor.
     * @returns {boolean}
     */
    isReturningUser() {
        return this.options.isReturning;
    }

    /**
     * Returns the number of page views in the current session.
     * Always checks cookie
     * @returns {number}
     */
    getPageViewsCount() {
        const cookieValues = this.getCookieValues();
        const cookieCount = cookieValues[PAGE_VIEW_COUNT_INDEX];
        this.options.pageViewCount = Math.max(
            cookieCount ? cookieCount : 1,
            this.options.pageViewCount
        );
        return this.options.pageViewCount;
    }

    /**
     * Returns a the current pageView Id
     * @returns {String}
     */
    getPageViewId() {
        return this.options.pageViewId;
    }

    /**
     * Returns a new pageViewId value
     */
    getNewPageViewId() {
        const newPageViewId = uuid.uuid4();
        return newPageViewId;
    }

    // -------------------------------------------------------------------------

    /**
     * These methods should only be called in from one place:
     *
     *     `utils/history`
     *
     * Responsible for maintaining state of `pageViewId` values
     * consistent with the browser history
     */

    /**
     * Generates and sets a new `pageViewId` value
     */
    generatePageViewId() {
        this.setPageViewId(this.getNewPageViewId());
    }

    /**
     * Sets the current value of `pageViewId`
     */
    setPageViewId(pageViewId) {
        if (this.options.pageViewId !== pageViewId) {
            this.options.pageViewId = pageViewId;
            this.trigger("pageViewIdChanged");
        }
    }

    // -------------------------------------------------------------------------

    /**
     * Returns a Promise that will be resolved with
     * the Google Analytics client id.
     * @returns {Promise}
     */
    getGAClientID() {
        return googleAnalyticsTransport.getClientId();
    }

    /**
     * Looks for all old cookies and returns the values of the latest ones
     * @returns {array}
     */
    getCookieValues() {
        let values = "";

        this.getCookieNames().forEach((name) => {
            let cookieValues = cookie(name);
            if (cookieValues) {
                values = cookieValues;
            }
        });

        return values.split(":");
    }

    /**
     * Retruns a array of all cookie names we used ending with the current version
     * @returns {array}
     */
    getCookieNames() {
        // we need the old, non version cookie name as a fallback
        let names = ["analytics"];

        _.range(COOKIE_VERSION + 1).forEach(function(index) {
            names.push(COOKIE_NAME + index);
        });

        return names;
    }

    getCurrentCookieName() {
        return COOKIE_NAME + COOKIE_VERSION;
    }

    /**
     * Returns an object containing information about the referring page view.
     * @returns {object}
     * @private
     */
    getReferrer() {
        const referrerMap = storage.get("referrerMap", {});

        return (
            referrerMap[this.referrer] || {
                href: this.referrer,
                pageViewId: null,
            }
        );
    }

    /**
     * Adds an entry to the referrer map.
     * @param {string} href
     * @param {string} pageViewId
     * @private
     */
    setReferrer(href, pageViewId) {
        this.referrer = href;

        const referrerMap = storage.get("referrerMap", {});
        referrerMap[href] = { href, pageViewId };

        storage.set("referrerMap", referrerMap);
    }

    getInitialPageType() {
        return storage.get("initialPageType", null, true);
    }
    /**
     * Sets an initial pagetype into session storage, if one isn't already present.
     * @param {string} pageType
     */
    _initInitialPageType(pageType) {
        const initialPageType = this.getInitialPageType();
        if (!initialPageType) {
            this.setInitialPageType(pageType);
        }
    }

    setInitialPageType(pageType) {
        storage.set("initialPageType", pageType, true);
    }

    getInitialSubPageType() {
        return storage.get("initialSubPageType", null, true);
    }
    /**
     * Sets an initial pagetype into session storage, if one isn't already present.
     * @param {string} pageType
     */
    _initInitialSubPageType(pageType) {
        const initialPageType = this.getInitialSubPageType();
        if (!initialPageType) {
            this.setInitialSubPageType(pageType);
        }
    }

    setInitialSubPageType(pageType) {
        storage.set("initialSubPageType", pageType, true);
    }

    /**
     * The analytics cookie needs to be updated to reflect the current page count,
     * so that this information can get to the back end.
     */
    updatePageViewInAnalyticsCookie() {
        var cookieValues = this.getCookieValues();
        var currentCookieName = this.getCurrentCookieName();

        cookieValues[PAGE_VIEW_COUNT_INDEX] = this.options.pageViewCount || 0;

        var finalCookie = '"' + cookieValues.join(":") + '"';

        cookie(currentCookieName, finalCookie, {
            expires: 365,
            path: "/",
            withEncoding: false,
            domain: this._getCookieDomain(),
        });
    }

    /**
     * Creates a new promise, adds it to pendingPromiseResolvers,
     * schedules a send, and returns the promise.
     * @returns {Promise}
     * @private
     */
    _addPromiseAndScheduleSend() {
        return new Promise((resolve) => {
            this.pendingPromiseResolvers.push(resolve);

            // only schedule a send if the first page view has been tracked
            if (this.options.pageViewCount > 0) {
                this._scheduleSend();
            }
        });
    }

    /**
     * Generates a page view object.
     * @returns {Object}
     * @private
     */
    _getPageViewData() {
        const referrer = this.getReferrer();
        const params = new URLSearchParams(globals.window.location.search);

        return this._addMetaData({
            // metadata
            _version: "0.0.4",
            _type: "page_views",

            // user data
            returning_user: this.isReturningUser(),
            country: environment.get("country", ""),
            language: environment.get("language", ""),
            domain: this._getCookieDomain(),
            signed_in: environment.get("userSignedInStatus", "no"),

            // product data
            product_id: environment.get("analyticsProduct.id", null),

            // page data
            page_type: environment.get("pageType"),
            page_sub_type: environment.get("pageSubType"),
            initial_page_type: this.getInitialPageType(),
            initial_page_sub_type: this.getInitialSubPageType(),

            // URL related fields
            url: globals.window.location.href,
            url_host: globals.window.location.host,
            url_query: globals.window.location.search.slice(1),
            url_path: globals.window.location.pathname,

            referrer: referrer.href,
            referrer_page_view_id: referrer.pageViewId,
            causative_user_action: getLastParamOrNull(params, "reason"),

            // search terms
            search_term: getLastParamOrNull(params, "term"),
            autosuggest_term: getLastParamOrNull(params, "q"),

            // variables needed for GA
            gaVariables: environment.get("gaVariables"),

            // device data
            user_agent: navigator.userAgent,

            // its save to use the actual `window` here
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            window_width: browser.getWindowWidth(),
            window_height: browser.getWindowHeight(),
            device_pixel_ratio: window.devicePixelRatio || 1,

            // Paid traffic data
            utm_medium: getLastParamOrNull(params, "utm_medium"),
            utm_campaign: getLastParamOrNull(params, "utm_campaign"),
            utm_source: getLastParamOrNull(params, "utm_source"),
            utm_content: getLastParamOrNull(params, "utm_content"),
            utm_term: getLastParamOrNull(params, "utm_term"),
            utm_country: getLastParamOrNull(params, "utm_country"),
            utm_grouping: getLastParamOrNull(params, "utm_grouping"),
            utm_keywords: getLastParamOrNull(params, "utm_keywords"),

            // Paid traffic data
            atc_medium: getLastParamOrNull(params, "atc_medium"),
            atc_campaign: getLastParamOrNull(params, "atc_campaign"),
            atc_country: getLastParamOrNull(params, "atc_country"),
            atc_grouping: getLastParamOrNull(params, "atc_grouping"),
            atc_keywords: getLastParamOrNull(params, "atc_keywords"),
            atc_source: getLastParamOrNull(params, "atc_source"),
            atc_content: getLastParamOrNull(params, "atc_content"),
            atc_term: getLastParamOrNull(params, "atc_term"),
            atc_remarketing: getLastParamOrNull(params, "atc_remarketing"),

            // Paid SEM Ids
            sem_id: getLastParamOrNull(params, "sem_id"),

            // PAID-1986 google clicks id
            gclid: getLastParamOrNull(params, "gclid"),

            // PAID-907 cannibalisation test
            paid_no_retarget: environment.get("paidNoRetarget"),

            // life cycle data
            life_cycle_stage: environment.get("lifeCycleStage"),

            heimdall_flags: environment.get("heimdallFlags"),

            // feature support
            supports_local_storage: browser.supportsLocalStorage,
            supports_touch: browser.hasTouch,
        });
    }

    /**
     * Sends the current `pendingData` to all transports defined in `transports`.
     * When all defined transports have indicated they have sent their
     * data then all Promises in `pendingPromiseResolvers` are resolved.
     * @private
     */
    _send() {
        if (globals.document.readyState !== "complete") {
            globals.window.addEventListener("load", this._scheduleSend.bind(this), false);
            return;
        }

        // create local copies of both data and promises array so they
        // can continue to be used without affecting this functions scope
        let data = _.clone(this.pendingData);
        let promises = _.clone(this.pendingPromiseResolvers);

        // create a wrapper promise for when all transports are done
        let allDataSent = Promise.all(
            _.map(this.transports, (transport) => {
                return transport.send(data);
            })
        );

        // reset global data and promises array
        this.pendingData.length = 0;
        this.pendingPromiseResolvers.length = 0;

        // setup a timeout in case things go wrong
        var timeout;

        function resolvePromises() {
            clearTimeout(timeout);
            promises.forEach((resolve) => resolve());
        }

        timeout = setTimeout(resolvePromises, 5000);

        // when all the data is sent resolve the promises
        allDataSent.then(resolvePromises).catch(resolvePromises);
    }

    /**
     * Schedules current analytics data to be sent when the current execution stack is complete.
     * This allows multiple analytics events being generated from different modules to be batched together.
     * If a call to `_send()` has already been scheduled but not yet called it is cancelled and
     * a new call scheduled.
     *
     * The use of setTimeout with a delay value of 0ms causes the function to be
     * executed when the current execution stack completes. Using 0ms is indicating intention
     * for 'as-soon-as-possible' behavior, all modern browsers will treat this as the minimum
     * timeout value (4ms).
     *
     * https://developer.mozilla.org/en-US/docs/Web/API/window.setTimeout#Minimum_delay_and_timeout_nesting
     * @private
     */
    _scheduleSend() {
        clearTimeout(this.sendTimeout);
        this.sendTimeout = setTimeout(this._send, 0);
    }

    /**
     * Enriches the passed object with common meta data that all
     * objects being added to `pendingData` should contain.
     * @param {Object} obj The object to enrich with meta data
     * @returns {Object}
     * @private
     */
    _addMetaData(obj) {
        return _.extend(obj, {
            _dt: this._getUTCTime(),
            _uuid: uuidv4(),
            _source: "lyst_web.frontend.analytics",
            _status: "v",
            session_id: this.getSessionId(),
            device_id: this.getDeviceUid(),
            user_id: environment.get("userId", ""),
            page_view_count: this.getPageViewsCount(),
            pageview_id: this.getPageViewId(),
            event_timestamp: this._getUTCTime(),
            release: environment.get("release"),
            release_sha: environment.get("release_sha"),
        });
    }

    /**
     * Returns the current UTC time string in the format "YYYY-MM-DDTHH:mm:ss.SSSZ"
     * Value is based off the reported time on page + given server time
     * @param [d] Used for tests
     * @returns {string}
     * @private
     */
    _getUTCTime(d) {
        let now = d || new Date();
        let browserTimeStart = this._browserTimeStart;

        // Server time is seconds since epoch
        let serverTimeStart = parseInt(environment.get("timestamp_ms"), 10);

        // Browser time delta is milliseconds since epoch
        let browserDelta = now - browserTimeStart;

        // Milliseconds since epoch using servertime and the browser delta
        let timestampMilliseconds = serverTimeStart + browserDelta;
        let timestamp = new Date(timestampMilliseconds);

        return timestamp.toISOString();
    }

    /**
     * Deletes all but the most recent version of the cookie.
     * @private
     */
    _deleteOldCookies() {
        let cookieNames = this.getCookieNames();
        let currentCookieName = this.getCurrentCookieName();

        cookieNames.forEach((name) => {
            if (name === currentCookieName) {
                return;
            }

            // we have to do it twice to remove the one with subdomain and without
            cookie(name, "", {
                expires: 0,
                path: "/",
            });

            cookie(name, "", {
                expires: 0,
                path: "/",
                domain: this._getCookieDomain(),
            });
        });
    }

    /**
     * Creates and stores a new analytics cookie.
     */
    _createCookie() {
        let cookieValues = [
            uuid.uuid4(), // Session ID
            1, // Page view count
            0, // Is returning user flag
            0, // Mystery value. Last visit date?
            uuid.uuid4(), // Device ID
        ];

        let cookieData = '"' + cookieValues.join(":") + '"';

        let currentCookieName = this.getCurrentCookieName();

        cookie(currentCookieName, cookieData, {
            expires: 365,
            path: "/",
            withEncoding: false,
            domain: this._getCookieDomain(),
        });

        return cookieValues;
    }

    /**
     * Reads and parses the analytics cookie.
     * @private
     */
    _setInitialState() {
        let cookieValues = this.getCookieValues();

        this._deleteOldCookies();

        if (!cookieValues[SESSION_ID_INDEX]) {
            cookieValues = this._createCookie();
        }

        let deviceId = cookieValues[DEVICE_UID_INDEX] || "";

        this.options = {
            sessionId: cookieValues[SESSION_ID_INDEX],
            pageViewCount: cookieValues[PAGE_VIEW_COUNT_INDEX],
            isReturning: cookieValues[IS_RETURNING_INDEX] === "1",
            deviceId: deviceId,
            pageViewId: environment.get("pageViewId"),
        };

        if (!this.options.pageViewCount) {
            // If there is no count in the cookie, initialise as 1
            this.options.pageViewCount = 1;
            this.updatePageViewInAnalyticsCookie();
        } else {
            // Otherwise, allow incrementing
            this._allowPageViewsCountIncrement = true;
        }
    }

    /**
     * Returns the host name without any subdomain
     * It is important that this domain string matches the one set by the backend
     * See - src/ssaw/portal/middleware/analytics.py
     * @returns {string}
     * @private
     */
    _getCookieDomain() {
        var hostnameParts = globals.window.location.hostname.split(".");

        if (
            _.contains(hostnameParts, "www") ||
            _.contains(hostnameParts, "checkout") ||
            _.contains(hostnameParts, "lystpreviews")
        ) {
            return hostnameParts.slice(1).join(".");
        }
    }

    /**
     * Calls `init()` on any transports that define that method.
     * @private
     */
    _initTransports() {
        this.transports.forEach((transport) => {
            if (_.isFunction(transport.init)) {
                transport.init(this);
            }
        });
    }
}

// create an instance
let analytics = new Analytics();

// expose the class for testing
analytics.Analytics = Analytics;

// return the instance as the modules export
export default analytics;
